import React, { useEffect, useMemo, useState } from 'react';
import { Segment } from 'tea-component';
import CardCommon from '@/pages/opinion/components/CardCommon';
import { nikkeTimeLineTypeMap, nikkeTimelineTypeOptions } from '../constants';
import { IconType, SymbolType } from '@op/constants/chart';
import { useTimelineDataLocalParams } from '@op/pages/Summary/hooks/useTimelineData';
import BarAndLine from '@op/pages/Summary/TopicTracking/TopicChartInfo/TopicTrends/BarAndLine';
import PieChart from './PieCharts';
import { TableColorType } from '@op/hooks/useTableSelect';
import { downloadAction } from '@op/pages/download/utils';
import { HeroMapType } from '@op/pages/Hero/utils';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import useLanguage from '@op/hooks/useLanguage';

export default function NIKKEChart({
  beforeLoading,
  ...props
}: {
  filter: any;
  transHeroInfo: (key: string) => { name: string; icon: string };
  selectedKeysMap: TableColorType;
  beforeLoading?: boolean;
  gameInfo: any;
  heroInfoMap: HeroMapType;
}) {
  return (
    <CardCommon
      contentCard={!beforeLoading}
      loading={beforeLoading}
      loadingHeight={310}
    >
      <Chart {...props}></Chart>
    </CardCommon>
  );
}

export function Chart({
  filter,
  transHeroInfo,
  selectedKeysMap,
  gameInfo,
  heroInfoMap,
}: {
  filter: any;
  transHeroInfo: (key: string) => { name: string; icon: string };
  selectedKeysMap: TableColorType;
  gameInfo: any;
  heroInfoMap: HeroMapType;
}) {
  const { formatOptionsMessage } = useLanguage({
    placement: 'top',
    showIcon: false,
  });
  const [operations, setOperations] = useState([]);
  const [chartType, setChartType] = useState<SymbolType>('line');
  const [title, setTitle] = useState('');
  // const [visible, setVisible] = useState(false);
  const {
    blockData,
    barAndLine,
    loading,
    apiError,
    tableInfo,
    indicators,
    setIndicators,
  } = useTimelineDataLocalParams({
    filter,
    topicLevel: 'L2',
    isNikke: true,
    dealwithParams: (value, indicators) => {
      let params: any = { top_n: 20, need_others: false };
      if (indicators === 'engagement_ratio') {
        params.page_source = 'nikke';
      }
      return { ...value, ...params };
    },
  });

  useEffect(() => {
    const { operations, text } = nikkeTimeLineTypeMap[indicators];
    setOperations(operations);
    if (
      operations.findIndex((item: IconType) => item.symbol === chartType) === -1
    ) {
      setChartType(operations[0].symbol);
    }
    setTitle(text);
  }, [indicators]);

  const lineData = useMemo(() => {
    const { seriesDataArray } = barAndLine || {};

    return seriesDataArray.map((item: any) => {
      const colorInfo = selectedKeysMap[item.name];
      return { ...item, color: colorInfo?.color };
    });
  }, [selectedKeysMap, barAndLine]);

  return (
    <CardCommon
      title={
        <Segment
          value={indicators}
          onChange={(value: any) => {
            buriedBtnPoint('MetricsTab', value);
            setIndicators(value);
          }}
          className="dbo-hero-segment"
          options={formatOptionsMessage(nikkeTimelineTypeOptions)}
        />
      }
      logBtnId="nikkeChart"
      extraInfo={indicators}
      loading={loading}
      loadingHeight={310}
      operations={operations}
      empty={filter.topic.length === 0}
      emptyHeight={310}
      operationInfo={[chartType, setChartType]}
      hasDownload
      handleDownloadOnClick={() =>
        downloadAction({
          fileName: 'Nikke',
          key: 'NikkeTimeline',
          filter,
          data: tableInfo?.data || [],
          auxiliaryParams: {
            header: tableInfo?.header || [],
            heroInfoMap,
            indicators,
          },
        })
      }
    >
      {['bar', 'line'].includes(chartType) ? (
        <BarAndLine
          chartType={chartType}
          height={310}
          sumMap={barAndLine.sumMap}
          dateType={filter.date_type}
          xAxisData={barAndLine.xAxisData}
          seriesDataArray={lineData}
          transTopic={(key: string) => {
            const { name, icon } = transHeroInfo(key);
            return name;
          }}
          indicators={indicators}
          noShow={[
            'positive_mentions',
            'negative_mentions',
            'sentiment',
            'engagement_ratio',
          ].includes(indicators)}
          title={title}
        ></BarAndLine>
      ) : (
        <PieChart
          transHeroInfo={transHeroInfo}
          filter={filter}
          data={blockData}
          selectedKeysMap={selectedKeysMap}
        ></PieChart>
      )}
    </CardCommon>
  );
}
